/**
 * Pure aggregation for the Meal Timing Dashboard on the Summary tab.
 *
 * Two derivations off the patient's `Meal[]` (from `useMeals`):
 *   • `buildMealTimingMatrix` — a weekday × hour-of-day frequency grid that
 *     drives the heatmap.
 *   • `computeMealTimingKpis` — avg first/last meal time + meals-per-day.
 *
 * Kept framework-free (no React, no i18n) so it's unit-testable in isolation,
 * mirroring how `glucoseTimeInRange` / `changeContext` are structured.
 */
import type { Meal, MealName } from '@/api/meals';

export type MealTypeFilter = MealName | 'all';

/** Heatmap x-axis window: 6 AM through the 10 PM bucket (covers 22:00–22:59). */
export const HEATMAP_START_HOUR = 6;
export const HEATMAP_END_HOUR = 22;

/** Hour columns rendered on the x-axis, inclusive of both ends. */
export const HEATMAP_HOURS: number[] = Array.from(
  { length: HEATMAP_END_HOUR - HEATMAP_START_HOUR + 1 },
  (_, i) => HEATMAP_START_HOUR + i,
);

/**
 * Monday-first row order. Values are `Date.getDay()` codes (0=Sun … 6=Sat);
 * the array index is the heatmap row (0=Mon … 6=Sun). `weekdayRow` maps a
 * date's getDay() back to its row via `indexOf`.
 */
export const WEEKDAY_ORDER: number[] = [1, 2, 3, 4, 5, 6, 0];

export type MealTimingMatrix = {
  /** counts[row][col] — row 0=Mon…6=Sun, col indexes `HEATMAP_HOURS`. */
  counts: number[][];
  /**
   * Distinct logged dates per weekday row (0=Mon…6=Sun), counting ANY
   * non-deleted meal regardless of the active type filter — i.e. the set of
   * days the patient was tracking on that weekday. This is the denominator for
   * `intensity`. Counting all meals (not just the filtered type) keeps the
   * consistency read honest: breakfast intensity means "share of tracked
   * Mondays with an 8 AM breakfast", not the trivially-high "share of Mondays
   * that already had a breakfast".
   */
  loggedDaysByRow: number[];
  /**
   * intensity[row][col] in [0,1] — the share of that weekday's logged days with
   * a meal in this hour (`counts / loggedDaysByRow`, capped at 1). 0 when the
   * row has no logged days. This drives the heatmap shading, so the color reads
   * as eating-rhythm *consistency* (1 = "every logged {weekday}, like
   * clockwork") rather than raw volume — which would otherwise inflate simply
   * because a longer date range accumulates more meals.
   */
  intensity: number[][];
  /** Largest single-cell count; 0 when there's no data. Retained for callers
   *  and tests that want the raw peak. */
  max: number;
  /** Total meals counted into the grid (after the type filter). */
  total: number;
};

export type MealTimingKpis = {
  /** Avg (across logged days) of each day's earliest meal, minutes from midnight. */
  avgFirstMealMinutes: number | null;
  /** Avg of each day's latest meal, minutes from midnight. */
  avgLastMealMinutes: number | null;
  /** Mean meals per logged day, rounded to one decimal (e.g. 4.2). */
  mealsPerDay: number | null;
  /** Distinct days with at least one (matching) meal. */
  loggedDays: number;
};

/** Hour-of-day from an `HH:MM[:SS]` meal_time. Null when unparseable. */
const parseHour = (mealTime: string): number | null => {
  if (!mealTime) return null;
  const h = Number(mealTime.split(':')[0]);
  return Number.isFinite(h) ? h : null;
};

/** Minutes-from-midnight from an `HH:MM[:SS]` meal_time. Null when unparseable. */
const parseMinutes = (mealTime: string): number | null => {
  if (!mealTime) return null;
  const [h, m] = mealTime.split(':');
  const hh = Number(h);
  if (!Number.isFinite(hh)) return null;
  const mm = Number(m ?? '0');
  return hh * 60 + (Number.isFinite(mm) ? mm : 0);
};

/** meal_date (YYYY-MM-DD) → Monday-first row index (0=Mon…6=Sun). Null if bad. */
const weekdayRow = (mealDate: string): number | null => {
  if (!mealDate) return null;
  const d = new Date(`${mealDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const row = WEEKDAY_ORDER.indexOf(d.getDay());
  return row === -1 ? null : row;
};

const matching = (meals: readonly Meal[], filter: MealTypeFilter): Meal[] =>
  meals.filter((m) => !m.is_deleted && (filter === 'all' || m.meal_name === filter));

export function buildMealTimingMatrix(
  meals: readonly Meal[],
  filter: MealTypeFilter,
): MealTimingMatrix {
  const counts: number[][] = Array.from({ length: 7 }, () => HEATMAP_HOURS.map(() => 0));
  let max = 0;
  let total = 0;

  for (const m of matching(meals, filter)) {
    const row = weekdayRow(m.meal_date);
    const hour = parseHour(m.meal_time);
    if (row === null || hour === null) continue;
    // Clamp out-of-window meals (pre-6 AM / post-10 PM) into the edge column
    // so rare early/late logs aren't silently dropped from the totals.
    const clamped = Math.min(Math.max(hour, HEATMAP_START_HOUR), HEATMAP_END_HOUR);
    const col = clamped - HEATMAP_START_HOUR;
    const next = counts[row][col] + 1;
    counts[row][col] = next;
    total += 1;
    if (next > max) max = next;
  }

  // Denominator for the consistency scale: distinct logged dates per weekday
  // over ALL non-deleted meals (any type) — the days the patient was tracking
  // on that weekday. See the `loggedDaysByRow` doc on MealTimingMatrix.
  const datesByRow: Array<Set<string>> = Array.from({ length: 7 }, () => new Set<string>());
  for (const m of matching(meals, 'all')) {
    const row = weekdayRow(m.meal_date);
    if (row === null || !m.meal_date) continue;
    datesByRow[row].add(m.meal_date);
  }
  const loggedDaysByRow = datesByRow.map((s) => s.size);

  const intensity = counts.map((rowCounts, row) => {
    const denom = loggedDaysByRow[row];
    return rowCounts.map((c) => (denom > 0 ? Math.min(1, c / denom) : 0));
  });

  return { counts, loggedDaysByRow, intensity, max, total };
}

export function computeMealTimingKpis(
  meals: readonly Meal[],
  filter: MealTypeFilter,
): MealTimingKpis {
  const byDay = new Map<string, { first: number; last: number; count: number }>();

  for (const m of matching(meals, filter)) {
    const mins = parseMinutes(m.meal_time);
    if (mins === null || !m.meal_date) continue;
    const cur = byDay.get(m.meal_date);
    if (!cur) {
      byDay.set(m.meal_date, { first: mins, last: mins, count: 1 });
    } else {
      cur.first = Math.min(cur.first, mins);
      cur.last = Math.max(cur.last, mins);
      cur.count += 1;
    }
  }

  const days = [...byDay.values()];
  if (days.length === 0) {
    return { avgFirstMealMinutes: null, avgLastMealMinutes: null, mealsPerDay: null, loggedDays: 0 };
  }

  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  const totalMeals = sum(days.map((d) => d.count));

  return {
    avgFirstMealMinutes: Math.round(sum(days.map((d) => d.first)) / days.length),
    avgLastMealMinutes: Math.round(sum(days.map((d) => d.last)) / days.length),
    mealsPerDay: Math.round((totalMeals / days.length) * 10) / 10,
    loggedDays: days.length,
  };
}
